import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLang } from "../i18n/LangContext";
import { api, extractError } from "../api/client";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AUTH } from "../constants/testIds";
import { ERROR_CODE_MAP } from "../i18n/translations";

export default function AcceptInvitation() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) return setError(t("validation.passwordMismatch"));
    setBusy(true);
    try {
      await api.post("/auth/accept-invitation", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      const { code, message } = extractError(err);
      const key = ERROR_CODE_MAP[code];
      setError(key ? t(`auth.${key}`) : message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <h1 className="font-[Outfit] text-3xl font-extrabold text-white tracking-tight">{t("auth.inviteAcceptTitle")}</h1>
        <p className="text-slate-400 text-sm">{t("auth.inviteAcceptSubtitle")}</p>
      </div>
      {done ? (
        <div className="space-y-4">
          <p data-testid="invite-accept-success" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-300">
            {t("auth.inviteAccepted")}
          </p>
          <Link to="/login" className="inline-block text-white font-semibold hover:text-[#FF3B30]">→ {t("auth.loginLink")}</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">{t("auth.newPassword")}</Label>
            <Input data-testid="invite-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="bg-[#1C202E] border-white/10 text-white h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300">{t("common.confirmPassword")}</Label>
            <Input data-testid="invite-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#1C202E] border-white/10 text-white h-11" />
          </div>
          {error && <div data-testid="invite-error-msg" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>}
          <Button data-testid="invite-submit-btn" type="submit" disabled={busy || !token} className="w-full h-11 bg-[#FF3B30] hover:bg-[#E02E24] text-white font-semibold">
            {busy ? t("common.loading") : t("auth.inviteSetPassword")}
          </Button>
        </form>
      )}
    </div>
  );
}
